import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Logo from './Logo';
import { XIcon } from './Icons';
import { APP_CONFIG } from '../data/content';

const Footer = ({ currentYear: propYear }) => {
  const [activeModal, setActiveModal] = useState(null);
  const currentYear = propYear || new Date().getFullYear();

  return (
    <footer className="py-12 border-t border-slate-200/80 dark:border-slate-800/80 bg-white/70 dark:bg-slate-950/70 backdrop-blur-md transition-colors duration-300 text-left">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-10">
          {/* Brand Info */}
          <div className="md:col-span-2 space-y-3">
            <div className="flex items-center gap-2.5">
              <Logo className="w-8 h-8" />
              <span className="text-lg font-bold text-slate-900 dark:text-white font-outfit">
                {APP_CONFIG.projectName}
              </span>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 font-light max-w-sm leading-relaxed">
              Autonomous multi-agent orchestration and distributed workflow engine built for high-throughput enterprise execution, hybrid RAG, and live SaaS transactions.
            </p>
            <div className="text-[11px] font-mono text-slate-400 pt-1">
              Powered by <span className="text-brand-500 font-semibold">{APP_CONFIG.engineName}</span>
            </div>
          </div>

          {/* Architecture Links */}
          <div className="space-y-2.5 text-xs">
            <h4 className="font-bold text-slate-900 dark:text-white font-outfit uppercase tracking-wider text-[11px]">
              Platform Architecture
            </h4>
            <ul className="space-y-1.5 text-slate-600 dark:text-slate-400 font-light">
              <li><a href="#simulator" className="hover:text-brand-500 transition-colors">DAG Orchestration Trace</a></li>
              <li><a href="#agents" className="hover:text-brand-500 transition-colors">6-Agent Fleet Matrix</a></li>
              <li><a href="#pillars" className="hover:text-brand-500 transition-colors">Hybrid Elasticsearch RAG</a></li>
              <li><a href="#integrations" className="hover:text-brand-500 transition-colors">SaaS & Postman Tool Registry</a></li>
              <li><a href="#sdk" className="hover:text-brand-500 transition-colors">Embeddable Chatbot SDK</a></li>
            </ul>
          </div>

          {/* Governance & Compliance */}
          <div className="space-y-2.5 text-xs">
            <h4 className="font-bold text-slate-900 dark:text-white font-outfit uppercase tracking-wider text-[11px]">
              Security & Legal
            </h4>
            <ul className="space-y-1.5 text-slate-600 dark:text-slate-400 font-light">
              <li>
                <button 
                  onClick={() => setActiveModal('security')}
                  className="hover:text-brand-500 transition-colors text-left"
                >
                  Zero-Trust Multi-Tenancy
                </button>
              </li>
              <li>
                <button 
                  onClick={() => setActiveModal('privacy')}
                  className="hover:text-brand-500 transition-colors text-left"
                >
                  Privacy Policy
                </button>
              </li>
              <li>
                <button 
                  onClick={() => setActiveModal('terms')}
                  className="hover:text-brand-500 transition-colors text-left"
                >
                  Terms of Service
                </button>
              </li>
              <li>
                <a 
                  href="mailto:contact@agentexpert.in"
                  className="hover:text-brand-500 transition-colors text-left"
                >
                  Engineering Support
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom copyright */}
        <div className="pt-6 border-t border-slate-200/80 dark:border-slate-800/80 flex flex-col sm:flex-row items-center justify-between text-xs text-slate-500 dark:text-slate-400 font-light gap-3">
          <p>
            &copy; {currentYear} {APP_CONFIG.projectName} &bull; {APP_CONFIG.domain}. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5 font-mono text-[11px] text-emerald-500">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Engine Online (v3.2)
            </span>
          </div>
        </div>
      </div>

      {/* Legal & Compliance Modals */}
      <AnimatePresence>
        {activeModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setActiveModal(null)}
              className="absolute inset-0 bg-slate-950/70 backdrop-blur-md"
            />
            
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              className="relative max-w-xl w-full glass-panel rounded-3xl p-6 sm:p-8 z-10 shadow-2xl border border-slate-200 dark:border-slate-800 text-left font-inter text-slate-800 dark:text-slate-200"
            >
              <button 
                onClick={() => setActiveModal(null)}
                className="absolute top-5 right-5 p-2 rounded-xl text-slate-400 hover:text-slate-600 dark:hover:text-white"
              >
                <XIcon className="w-5 h-5" />
              </button>

              {activeModal === 'privacy' && (
                <div className="space-y-3">
                  <h3 className="text-xl font-bold font-outfit text-slate-900 dark:text-white">
                    Privacy & Data Isolation Policy
                  </h3>
                  <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed font-light">
                    Supreme Adventure enforces strict tenant-level isolation across all indexed vectors, embeddings, documents, and tool executions. No customer data is shared between tenants or used to train third-party foundation models.
                  </p>
                </div>
              )}

              {activeModal === 'terms' && (
                <div className="space-y-3">
                  <h3 className="text-xl font-bold font-outfit text-slate-900 dark:text-white">
                    Enterprise Terms of Service
                  </h3>
                  <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed font-light">
                    Deployments under {APP_CONFIG.domain} are governed by enterprise SLAs. Tool execution is audited with mandatory Critic Agent validation and transaction rollbacks for verified compliance.
                  </p>
                </div>
              )}

              {activeModal === 'security' && (
                <div className="space-y-3">
                  <h3 className="text-xl font-bold font-outfit text-slate-900 dark:text-white">
                    Zero-Trust Multi-Tenancy Architecture
                  </h3>
                  <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed font-light">
                    Every incoming HTTP/WebSocket request is bound to a validated <code>X-Tenant-ID</code> token stored in ThreadLocal memory. Elasticsearch vector searches and MongoDB queries automatically inject tenant filters into the query pipeline to prevent unauthorized cross-tenant data leakage.
                  </p>
                </div>
              )}

              <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-800 flex justify-end">
                <button
                  onClick={() => setActiveModal(null)}
                  className="px-4 py-2 rounded-xl bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-xs font-semibold text-slate-800 dark:text-slate-200 transition-colors"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </footer>
  );
};

export default Footer;
